/**
 * Profile API
 * HTTP API calls for user profile and persisted settings (DynamoDB)
 */

import apiClient from '../../../../shared/lib/apiClient';

export interface Profile {
  name?: string;
  address?: string;
  email?: string;
  user_id?: string;
  /** Persisted app settings (API keys, AWS, LLM config, tech stack). Stored in DynamoDB when authenticated. */
  app_settings?: Record<string, unknown>;
  /** Persisted GitHub settings (repo, branch, token). Stored in DynamoDB when authenticated. */
  github_settings?: Record<string, unknown>;
}

export interface UpdateProfileRequest {
  name?: string;
  address?: string;
  user_id?: string;
  app_settings?: Record<string, unknown>;
  github_settings?: Record<string, unknown>;
}

export interface UpdateProfileResponse {
  message?: string;
  profile?: Profile;
}

/**
 * Get user profile by user ID (includes app_settings and github_settings when persisted).
 */
export async function getProfile(userId: string): Promise<Profile> {
  return apiClient<Profile>(`/userprofile/${userId}`, {
    method: 'GET',
    requireAuth: true
  });
}

/**
 * Update user profile (supports partial updates: name, address, app_settings, github_settings).
 */
export async function updateProfile(data: UpdateProfileRequest): Promise<UpdateProfileResponse | Profile> {
  return apiClient<UpdateProfileResponse | Profile>('/userprofile', {
    method: 'POST',
    body: data,
    requireAuth: true
  });
}

/**
 * Persist app settings to DynamoDB for the current user. Also updates localStorage as cache.
 * Use when authenticated; falls back to localStorage-only when not.
 */
export async function updateSettings(
  userId: string,
  appSettings: Record<string, unknown>,
  githubSettings?: Record<string, unknown>
): Promise<void> {
  await updateProfile({
    user_id: userId,
    app_settings: appSettings,
    ...(githubSettings != null && { github_settings: githubSettings })
  });
}
